import { OperationOutcomeError } from '@medplum/core';

export function isOperationOutcome(error: unknown): error is OperationOutcomeError {
  return (
    !!error &&
    typeof error === 'object' &&
    'outcome' in (error as any) &&
    'resourceType' in (error as any).outcome &&
    (error as any).outcome.resourceType === 'OperationOutcome'
  );
}

export function getOperationOutcomeMessage(error: OperationOutcomeError | Error | unknown): string {
  if (isOperationOutcome(error)) {
    const issue = error.outcome.issue?.[0];
    const text = issue?.details?.text ?? issue?.diagnostics;
    if (text) {
      return text;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Unknown error';
}

export function isVersionConflictOutcome(error: unknown): boolean {
  if (!isOperationOutcome(error)) {
    return false;
  }
  const outcome = error.outcome;

  const text = outcome.issue?.[0]?.details?.text ?? '';
  return typeof text === 'string' && text.startsWith('Test failed:');
}

/**
 * Detects errors where the server failed to read a WebSocket subscription resource
 * Subscription resource (e.g. because it was deleted or expired). Medplum's
 * `$get-ws-binding-token` endpoint returns the following error message:
 * "Error reading subscription: Not found"
 */
export function isSubscriptionNotFoundError(error: unknown): boolean {
  let text = '';

  if (isOperationOutcome(error)) {
    text = error.outcome.issue?.[0]?.details?.text ?? '';
  } else if (typeof error === 'string') {
    text = error;
  }

  if (typeof text !== 'string' || !text) {
    return false;
  }

  const isSubscriptionNotFoundError = text === 'Error reading subscription: Not found';

  return isSubscriptionNotFoundError;
}

/**
 * Detects OperationOutcome errors where the user has exceeded the maximum
 * number of concurrent WebSocket subscriptions.
 */
export function isSubscriptionLimitError(error: unknown): boolean {
  if (!isOperationOutcome(error)) {
    return false;
  }

  const text = error.outcome.issue?.[0]?.details?.text ?? '';
  if (typeof text !== 'string') {
    return false;
  }

  return text.includes('User has exceeded allotted maximum number of concurrent WebSocket subscriptions');
}

/**
 * Detects errors where the server refused the request because the caller's
 * AccessPolicy does not grant the resource type. Medplum returns HTTP 403 with an
 * OperationOutcome whose issue code is "forbidden".
 */
export function isForbiddenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error as any;

  // Common fetch/axios-style error shapes
  if (err.status === 403 || err.statusCode === 403 || err.response?.status === 403) {
    return true;
  }

  if (isOperationOutcome(error)) {
    return err.outcome?.issue?.some((issue: any) => issue?.code === 'forbidden') ?? false;
  }

  return false;
}

export function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error as any;

  // Common fetch/axios-style error shapes
  if (err.status === 404 || err.statusCode === 404 || err.response?.status === 404) {
    return true;
  }

  // Medplum OperationOutcomeError can encode not-found via OperationOutcome issue code
  if (isOperationOutcome(error)) {
    const issueCode = err.outcome?.issue?.[0]?.code;
    if (issueCode === 'not-found') {
      return true;
    }
  }

  return false;
}
